import { useEffect, useMemo, useRef, useState } from 'react';
import type { DisplaySettings, StepPageBlock, StepPageContent } from '@template-project/funnels-contract';
import { useCampaignContext } from './campaign-provider';
import type { CampaignEvaluationContext, RuntimeCampaign } from './types';
import { resolveCampaignSet } from './campaigns-resolver';
import { campaignLogger } from './logger';

const DISMISSED_CAMPAIGNS_KEY = 'campaigns_dismissed_ids';

function decodeCookieComponent(value: string): string | null {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

function readCookies(): Record<string, string> {
  if (typeof document === 'undefined' || !document.cookie) return {};
  return document.cookie
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, pair) => {
      const separator = pair.indexOf('=');
      const key = decodeCookieComponent(separator >= 0 ? pair.slice(0, separator) : pair);
      const value = decodeCookieComponent(separator >= 0 ? pair.slice(separator + 1) : '');
      if (key && value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {});
}

function readDismissedCampaignIds(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(DISMISSED_CAMPAIGNS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string') : [];
  } catch {
    return [];
  }
}

function writeDismissedCampaignIds(ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(DISMISSED_CAMPAIGNS_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

export interface UseCampaignExperienceResult {
  loading: boolean;
  bannerCampaign: RuntimeCampaign | null;
  bannerPageContent: StepPageContent | StepPageBlock[] | null;
  bannerDisplaySettings: DisplaySettings | null;
  overlayCampaign: RuntimeCampaign | null;
  overlayPageContent: StepPageContent | StepPageBlock[] | null;
  overlayDisplaySettings: DisplaySettings | null;
  dismissCampaign: (campaignId: string) => void;
}

export function useCampaignExperience(
  pageType: string,
  context: CampaignEvaluationContext,
  activeFunnelExperience?: {
    experienceType: string | null;
    bannerPlacement?: { position?: string | null; mobileElementId?: string | null; desktopElementId?: string | null } | null;
  } | null,
): UseCampaignExperienceResult {
  const { client } = useCampaignContext();
  const [loading, setLoading] = useState(true);
  const [bannerCampaign, setBannerCampaign] = useState<RuntimeCampaign | null>(null);
  const [bannerPageContent, setBannerPageContent] = useState<StepPageContent | StepPageBlock[] | null>(null);
  const [bannerDisplaySettings, setBannerDisplaySettings] = useState<DisplaySettings | null>(null);
  const [overlayCampaign, setOverlayCampaign] = useState<RuntimeCampaign | null>(null);
  const [overlayPageContent, setOverlayPageContent] = useState<StepPageContent | StepPageBlock[] | null>(null);
  const [overlayDisplaySettings, setOverlayDisplaySettings] = useState<DisplaySettings | null>(null);
  const [dismissedIds, setDismissedIds] = useState<string[]>(() => readDismissedCampaignIds());
  const dismissedIdsRef = useRef(dismissedIds);

  useEffect(() => {
    dismissedIdsRef.current = dismissedIds;
  }, [dismissedIds]);

  const contextKey = useMemo(
    () =>
      JSON.stringify({
        pageType,
        funnelId: context.funnelId ?? '',
        currentUrl: context.currentUrl ?? '',
        landingUrl: context.landingUrl ?? '',
        referrer: context.referrer ?? '',
        location: context.location ?? '',
        country: context.country ?? '',
        cartToken: context.cartToken ?? '',
        cartTotal: context.cartTotal ?? null,
        cartItemCount: context.cartItemCount ?? null,
        cartProductIds: context.cartProductIds ?? [],
        orderId: context.orderId ?? '',
        orderTotal: context.orderTotal ?? null,
        orderProductIds: context.orderProductIds ?? [],
        urlParameters: context.urlParameters ?? {},
      }),
    [
      pageType,
      context.funnelId,
      context.currentUrl,
      context.landingUrl,
      context.referrer,
      context.location,
      context.country,
      context.cartToken,
      context.cartTotal,
      context.cartItemCount,
      context.cartProductIds,
      context.orderId,
      context.orderTotal,
      context.orderProductIds,
      context.urlParameters,
    ],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setBannerPageContent(null);
    setBannerDisplaySettings(null);
    setOverlayPageContent(null);
    setOverlayDisplaySettings(null);

    Promise.resolve()
      .then(() =>
        client.evaluateCampaigns({
          pageType,
          funnelId: context.funnelId,
          context: {
            ...context,
            cookies: context.cookies ?? readCookies(),
            currentUrl:
              context.currentUrl ??
              (typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : ''),
            landingUrl:
              context.landingUrl ??
              (typeof window !== 'undefined' ? window.location.href : ''),
            referrer:
              context.referrer ??
              (typeof document !== 'undefined' ? document.referrer : ''),
            location:
              context.location ??
              context.country ??
              '',
          },
        }),
      )
      .then(async (response) => {
        if (cancelled) return;
        const resolved = resolveCampaignSet(
          response ?? { matched: false, matches: [] },
          dismissedIdsRef.current,
          activeFunnelExperience,
        );

        setBannerCampaign(resolved.bannerCampaign);
        setOverlayCampaign(resolved.overlayCampaign);

        if (!resolved.bannerCampaign && !resolved.overlayCampaign) {
          setLoading(false);
          return;
        }

        const [bannerTemplate, overlayTemplate] = await Promise.all([
          resolved.bannerCampaign?.templateId
            ? client.fetchTemplate(resolved.bannerCampaign.templateId).catch((error) => {
                campaignLogger.warn('Campaign template fetch failed; using fallback campaign content', {
                  campaignId: resolved.bannerCampaign?.id ?? null,
                  templateId: resolved.bannerCampaign?.templateId ?? null,
                  slot: 'banner',
                  error: error instanceof Error ? error.message : String(error),
                });
                return null;
              })
            : Promise.resolve(null),
          resolved.overlayCampaign?.templateId
            ? client.fetchTemplate(resolved.overlayCampaign.templateId).catch((error) => {
                campaignLogger.warn('Campaign template fetch failed; using fallback campaign content', {
                  campaignId: resolved.overlayCampaign?.id ?? null,
                  templateId: resolved.overlayCampaign?.templateId ?? null,
                  slot: 'overlay',
                  error: error instanceof Error ? error.message : String(error),
                });
                return null;
              })
            : Promise.resolve(null),
        ]);

        if (cancelled) return;

        setBannerPageContent(bannerTemplate?.pageContent ?? null);
        setBannerDisplaySettings(bannerTemplate?.displaySettings ?? null);
        setOverlayPageContent(overlayTemplate?.pageContent ?? null);
        setOverlayDisplaySettings(overlayTemplate?.displaySettings ?? null);

        if (resolved.bannerCampaign?.templateId && !bannerTemplate?.pageContent) {
          campaignLogger.warn('Campaign runtime is using fallback content because banner template has no renderable blocks', {
            campaignId: resolved.bannerCampaign.id,
            templateId: resolved.bannerCampaign.templateId,
            slot: 'banner',
          });
        }

        if (resolved.overlayCampaign?.templateId && !overlayTemplate?.pageContent) {
          campaignLogger.warn('Campaign runtime is using fallback content because overlay template has no renderable blocks', {
            campaignId: resolved.overlayCampaign.id,
            templateId: resolved.overlayCampaign.templateId,
            slot: 'overlay',
          });
        }

        setLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        campaignLogger.error(
          'Campaign evaluation failed; suppressing campaign rendering for this page',
          error instanceof Error ? error : undefined,
          {
            pageType,
            funnelId: context.funnelId ?? null,
            currentUrl: context.currentUrl ?? null,
            reason: 'evaluation_failed',
          },
        );
        setBannerCampaign(null);
        setBannerPageContent(null);
        setBannerDisplaySettings(null);
        setOverlayCampaign(null);
        setOverlayPageContent(null);
        setOverlayDisplaySettings(null);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeFunnelExperience?.bannerPlacement?.desktopElementId,
    activeFunnelExperience?.bannerPlacement?.mobileElementId,
    activeFunnelExperience?.bannerPlacement?.position,
    activeFunnelExperience?.experienceType,
    client,
    contextKey,
    pageType,
  ]);

  return {
    loading,
    bannerCampaign,
    bannerPageContent,
    bannerDisplaySettings,
    overlayCampaign,
    overlayPageContent,
    overlayDisplaySettings,
    dismissCampaign: (campaignId: string) => {
      if (!campaignId) return;
      const nextIds = dismissedIds.includes(campaignId) ? dismissedIds : [...dismissedIds, campaignId];
      dismissedIdsRef.current = nextIds;
      setDismissedIds(nextIds);
      writeDismissedCampaignIds(nextIds);
      if (bannerCampaign?.id === campaignId) {
        setBannerCampaign(null);
        setBannerPageContent(null);
        setBannerDisplaySettings(null);
      }
      if (overlayCampaign?.id === campaignId) {
        setOverlayCampaign(null);
        setOverlayPageContent(null);
        setOverlayDisplaySettings(null);
      }
    },
  };
}
